import React, { useEffect, useId, useRef, useState } from 'react';

/**
 * DropdownMenu — a trigger that opens a short list of actions.
 *
 * Props:
 *   renderTrigger ({ open, toggle, triggerProps }) => node
 *                 spread `triggerProps` on the trigger button (aria + click)
 *   items         { id, label, description?, icon?, danger?, onSelect }[]
 *                 or { type: 'separator' }
 *   align         'right' | 'left'   which edge of the trigger the menu lines up with
 *   width         Tailwind width class for the menu
 *
 * Closes on selection, outside click and Escape (focus returns to the
 * trigger); ArrowUp / ArrowDown move between items.
 */
export const DropdownMenu = ({ renderTrigger, items, align = 'right', width = 'w-44', label }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const menuItems = () => Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
  const focusTrigger = () => rootRef.current?.querySelector('[aria-haspopup="menu"]')?.focus();

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector('[role="menuitem"]')?.focus();
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const onMenuKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      focusTrigger();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const list = menuItems();
      const index = list.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      list[(next + list.length) % list.length]?.focus();
    }
  };

  const triggerProps = {
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: (event) => {
      event.stopPropagation();
      setOpen((value) => !value);
    },
  };

  return (
    <div ref={rootRef} className="relative inline-flex">
      {renderTrigger({ open, toggle: () => setOpen((value) => !value), triggerProps })}
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          onClick={(event) => event.stopPropagation()}
          className={[
            'absolute top-full mt-1 z-30 py-1 rounded-[10px] bg-white border border-black/[0.08] text-left',
            align === 'right' ? 'right-0' : 'left-0',
            width,
          ].join(' ')}
          style={{ boxShadow: 'var(--shadow-pop)' }}
        >
          {items.map((item, index) =>
            item.type === 'separator' ? (
              <div key={`separator-${index}`} role="separator" className="my-1 h-px bg-black/[0.06]" />
            ) : (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
                className={[
                  'w-full flex items-start gap-2.5 px-3 py-2 text-left text-[13px] bg-transparent border-0',
                  'outline-none hover:bg-black/[0.04] focus-visible:bg-black/[0.05]',
                  item.danger ? 'text-[#B85052]' : 'text-lb-ink',
                ].join(' ')}
              >
                {item.icon && <item.icon size={15} className="mt-[1px] flex-shrink-0 opacity-80" aria-hidden="true" />}
                <span className="min-w-0">
                  <span className="block font-medium">{item.label}</span>
                  {item.description && (
                    <span className="block text-[12px] text-lb-muted mt-0.5">{item.description}</span>
                  )}
                </span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};
